/**
 * Mutation request handler — server-only execution.
 * All mutations go through the server; local engine execution happens after server acknowledgement.
 */
import { getUserContext, ensureUserContext } from 'services/authentication/getUserContext';
import { isSessionValid, reportSessionLost } from 'services/session/sessionGuard';
import { isStale, resetActivityTimer } from 'services/staleness/stalenessGuard';
import { getLoginState, styleLogin } from 'services/authentication/loginState';
import { saveTournamentRecord } from 'services/storage/saveTournamentRecord';
import { notifyMutationApplied } from 'services/mutation/mutationObservers';
import { tmxToast } from 'services/notifications/tmxToast';
import { emitTmx } from 'services/messaging/socketIo';
import * as factory from 'tods-competition-factory';
import { serverConfig } from 'config/serverConfig';
import { debugConfig } from 'config/debugConfig';
import { isFunction } from 'functions/typeOf';
import { context } from 'services/context';
import dayjs from 'dayjs';
import { t } from 'i18n';

// types
import type { MutationMethod, ExecutionResult } from 'types/services';

// constants
import { SET_TOURNAMENT_DATES } from 'constants/mutationConstants';
import { SUPER_ADMIN, TOURNAMENT_ENGINE } from 'constants/tmxConstants';
import { isMutationAllowed } from '@courthive/provider-config';
import { providerConfig } from 'config/providerConfig';

interface MutationParams {
  tournamentRecord?: any;
  methods: MutationMethod[];
  engine?: string;
  callback?: (result: ExecutionResult) => void;
  /**
   * Ask the server to re-seed the per-event payloads this mutation evicts, so the first public
   * reader does not pay a cache miss. Set it when a reader is imminent — releasing a draw — and
   * leave it off otherwise, because building the payload is the cost the default path avoids.
   * Server-side only; the factory never sees it.
   */
  warmCache?: boolean;
}

// ── Pure helpers (testable without DOM) ──

export function checkOfflineState(tournamentRecords: Record<string, any>): {
  offline: number;
  invalidOffline: boolean;
} {
  const getOffline = (record: any) =>
    record.timeItems?.find(({ itemType }: any) => itemType === 'TMX')?.itemValue?.offline;
  const offlineValues = Object.values(tournamentRecords).map(getOffline).filter(Boolean);
  const invalidOffline =
    offlineValues.length > 1 && !offlineValues.every((v: any) => !v.email || v.email === offlineValues[0].email);
  return { offline: offlineValues.length, invalidOffline };
}

export function applyDevOverrides(methods: MutationMethod[], devParams?: Record<string, any>): MutationMethod[] {
  if (!devParams) return methods;
  return methods.map((m) => (devParams[m.method] ? { ...m, params: { ...m.params, ...devParams[m.method] } } : m));
}

const LOCAL_ONLY = 'local-only';
const LOCAL_FIRST = 'local-first';
const SERVER_FIRST = 'server-first';
type ExecutionStrategy = typeof LOCAL_ONLY | typeof LOCAL_FIRST | typeof SERVER_FIRST;

export function determineExecutionStrategy(
  hasProvider: boolean,
  offline: boolean,
  serverFirst: boolean,
): ExecutionStrategy {
  if (!hasProvider || offline) return LOCAL_ONLY;
  if (!serverFirst) return LOCAL_FIRST;
  return SERVER_FIRST;
}

export async function mutationRequest(params: MutationParams): Promise<void> {
  if (isStale()) {
    const msg = 'Please refresh tournament data before making changes';
    tmxToast({ message: msg, intent: 'is-warning' });
    if (params.callback) params.callback({ error: { message: msg } });
    return;
  }
  resetActivityTimer();

  const { tournamentRecord, methods, engine = TOURNAMENT_ENGINE, callback, warmCache } = params;
  const state = getLoginState();

  const completion = (result?: any): void => {
    if (tournamentRecord) factory[engine].reset();
    if (callback && isFunction(callback)) {
      callback(result);
    } else if (result?.error) {
      tmxToast({ message: result.error.message ?? t('common.error'), intent: 'is-danger' });
    }
  };

  if (!Array.isArray(methods)) return completion();
  const factoryEngine = factory[engine];
  if (!factoryEngine) return completion();

  // Provider-level mutation gating (defense-in-depth — server mirrors this in executionQueue)
  const currentPermissions = providerConfig.get().permissions ?? {};
  const blocked = methods.find((m) => !isMutationAllowed(m.method, currentPermissions));
  if (blocked) {
    return completion({ error: { message: `Action not permitted: ${blocked.method}` } });
  }

  if (tournamentRecord) factoryEngine.setState(tournamentRecord);

  const getProviderId = (tournamentRecord: any) => tournamentRecord?.parentOrganisation?.organisationId;
  const tournamentRecords = factoryEngine.getState()?.tournamentRecords ?? {};

  const { offline, invalidOffline } = checkOfflineState(tournamentRecords);
  if (invalidOffline) return tmxToast({ message: t('toasts.notAllOffline'), intent: 'is-danger' });

  const tournamentIds = Object.values(tournamentRecords)?.map((record: any) => record.tournamentId);
  let providerIds = factory.tools.unique(Object.values(tournamentRecords)?.map(getProviderId)).filter(Boolean);
  if (providerIds.length > 1) return tmxToast({ message: t('toasts.multipleProviders'), intent: 'is-danger' });

  // jim-tennis-deploy (50b60c51): fall back to login-state provider when the
  // tournament record lacks parentOrganisation. Required by the parks-cup
  // import pipeline which stages tournaments before assigning a provider.
  if (!providerIds.length) {
    const stateProviderId = state?.provider?.organisationId || state?.providerId;
    if (stateProviderId) providerIds = [stateProviderId];
  }

  const isDateChange = methods.some((m: any) => m.method === SET_TOURNAMENT_DATES);
  const now = new Date().getTime();
  const inDateRange =
    isDateChange ||
    Object.values(tournamentRecords).every((record: any) => {
      const endTime = dayjs(record.endDate).endOf('day').valueOf();
      return !!(endTime && endTime >= now);
    });

  const mutate = (saveLocal?: boolean) =>
    makeMutation({ offline, methods, factoryEngine, tournamentIds, completion, saveLocal, warmCache });
  if (!inDateRange) {
    queryDateRange({ state, providerIds, mutate });
    return;
  }
  if (providerIds.length && !offline && state) {
    checkPermissions({ state, providerIds, mutate }).catch((err) =>
      console.error('[mutationRequest] checkPermissions failed', err),
    );
    return;
  }

  // No provider and not logged in
  tmxToast({ message: t('toasts.notLoggedIn'), intent: 'is-warning' });
}

function queryDateRange({
  state,
  providerIds,
  mutate,
}: {
  state: any;
  providerIds: string[];
  mutate: (saveLocal?: boolean) => Promise<void>;
}): void {
  const onClick = () => (providerIds?.length ? checkPermissions({ state, providerIds, mutate }) : mutate());
  return tmxToast({
    action: { onClick, text: t('mutationPrompts.modify') },
    message: t('toasts.notInDateRange'),
    intent: 'is-danger',
    pauseOnHover: true,
    duration: 8000,
  });
}

async function checkPermissions({
  state,
  providerIds,
  mutate,
}: {
  state: any;
  providerIds: string[];
  mutate: (saveLocal?: boolean) => Promise<void>;
}): Promise<void> {
  if (!state) {
    context.provider = undefined;
    styleLogin(false);
    return tmxToast({ message: t('toasts.notLoggedIn'), intent: 'is-warning' });
  }

  const targetProviderId = providerIds[0];

  // Use the multi-provider userContext if available (preferred source —
  // reflects current user_providers state without JWT staleness). Lazy
  // fetch on the mutation path closes the race that produced "Not
  // authorized" toasts for users with NULL users.provider_id whose
  // userContext cache hadn't yet populated. Falling back to JWT fields
  // covers older sessions where /auth/me is unreachable.
  const userContext = (await ensureUserContext()) ?? getUserContext();
  const isProvider = userContext
    ? userContext.providerIds.includes(targetProviderId)
    : !!(
        state?.providerIds?.includes(targetProviderId) ||
        state?.providerAssociations?.some((a: any) => a.providerId === targetProviderId) ||
        state?.provider?.organisationId === targetProviderId
      );
  const isSuperAdmin = userContext ? userContext.isSuperAdmin : state?.roles?.includes(SUPER_ADMIN);
  const impersonating = context.provider?.organisationId === targetProviderId;

  if (!isProvider && !isSuperAdmin) return tmxToast({ message: t('toasts.notAuthorized'), intent: 'is-danger' });
  if (!isProvider && isSuperAdmin && !impersonating) {
    const impersonateProvider = () => {
      context.provider = { organisationId: targetProviderId, organisationName: '', organisationAbbreviation: '' };
      return mutate(false);
    };

    return tmxToast({
      action: {
        onClick: impersonateProvider,
        text: t('mutationPrompts.impersonate'),
      },
      message: t('toasts.superAdmin'),
      intent: 'is-danger',
    });
  }

  mutate();
}

function engineExecution({ factoryEngine, methods }: { factoryEngine: any; methods: any[] }): any {
  if (debugConfig.get().log?.verbose) console.log('%c executing locally', 'color: lightgreen');
  const directives = factory.tools.makeDeepCopy(methods);
  const result = factoryEngine.executionQueue(directives, true) || {};
  // Single choke point every mutationRequest passes through. Any successful
  // local mutation makes page-level factory-read caches (e.g. the schedule2
  // matchUp cache) stale — notify central observers so they invalidate
  // before any callback re-reads. Without this, score entry from the scoring
  // modal (which calls mutationRequest directly, not the schedule's
  // executeMethods) left the grid showing pre-score data after modal close.
  if (!result.error) notifyMutationApplied();
  return result;
}

async function localSave(saveLocal: boolean): Promise<void> {
  if (saveLocal || serverConfig.get().saveLocal) {
    await saveTournamentRecord();
  }
}

async function makeMutation({
  offline,
  methods,
  completion,
  factoryEngine,
  tournamentIds,
  saveLocal,
  warmCache,
}: {
  offline: any;
  methods: any[];
  completion: (result?: any) => void;
  factoryEngine: any;
  tournamentIds: string[];
  saveLocal?: boolean;
  warmCache?: boolean;
}): Promise<void> {
  const hasProvider = !!factoryEngine.getTournament().tournamentRecord?.parentOrganisation?.organisationId;
  const resolvedMethods = applyDevOverrides(methods, window['dev']?.params);
  if (window?.['dev']?.getContext().internal) console.log({ methods: resolvedMethods });

  const strategy = determineExecutionStrategy(hasProvider, !!offline, serverConfig.get().serverFirst);

  let factoryResult: any;
  if (strategy === LOCAL_ONLY || strategy === LOCAL_FIRST) {
    factoryResult = engineExecution({ factoryEngine, methods: resolvedMethods });
    if (factoryResult.error) return completion(factoryResult);
    if (strategy === LOCAL_ONLY) {
      await localSave(true);
      return completion(factoryResult);
    }
  }

  if (hasProvider && (factoryResult?.success || strategy === SERVER_FIRST)) {
    let ackReceived = false;
    let timedOut = false;
    const ackCallback = (ack: any) => {
      if (timedOut) return;
      ackReceived = true;
      const missingTournament = ack?.error?.code === 'ERR_MISSING_TOURNAMENT';
      if (serverConfig.get().serverFirst && (ack?.success || missingTournament)) {
        (async () => {
          // Replay any methods the server appended (e.g. attachPolicies for the
          // provider's participant-privacy policy on tournament creation) so the
          // local factory state matches what the server persisted. General
          // mechanism — the client does not need to know what the methods do.
          const serverMethods = Array.isArray(ack?.appliedServerMethods) ? ack.appliedServerMethods : [];
          const methodsToApply = serverMethods.length ? [...resolvedMethods, ...serverMethods] : resolvedMethods;
          factoryResult = engineExecution({ factoryEngine, methods: methodsToApply });
          if (factoryResult.error) return completion(factoryResult);
          await localSave(saveLocal || missingTournament);
          return completion(factoryResult);
        })();
      } else if (strategy === SERVER_FIRST) {
        completion(ack?.error ? ack : { error: { message: t('mutationPrompts.rejected') } });
      }
    };
    if (debugConfig.get().log?.verbose) console.log('%c invoking remote', 'color: lightblue');
    emitTmx({
      data: {
        type: 'executionQueue',
        payload: { methods: resolvedMethods, tournamentIds, rollbackOnError: true, warmCache },
      },
      ackCallback,
    });
    if (strategy === LOCAL_FIRST) {
      await localSave(saveLocal || false);
    } else {
      setTimeout(() => {
        if (ackReceived) return;
        timedOut = true;
        // Distinguish an auth failure from a genuine outage. A logged-out /
        // expired session produces the same "no ack" symptom as a down server,
        // but the fix is completely different: the user must log in again, and
        // the tournament they're looking at (loaded from IndexedDB) is fine.
        // Preserve the edit and replay it once the session is restored, rather
        // than dropping it and blaming the server.
        if (!isSessionValid()) {
          reportSessionLost({
            source: 'mutation-timeout',
            retry: () => replayServerMutation({ methods, factoryEngine, tournamentIds, saveLocal, warmCache }),
          });
          return completion({ error: { message: t('mutationPrompts.sessionExpired'), code: 'SESSION_EXPIRED' } });
        }
        tmxToast({ message: t('toasts.serverNotResponding'), intent: 'is-danger' });
        completion({ error: { message: t('toasts.serverNotResponding') } });
      }, serverConfig.get().serverTimeout ?? 10000);
    }
  }

  if (strategy === LOCAL_FIRST) return completion(factoryResult);
}

/**
 * Re-issue a server-first mutation that was preserved when the session expired.
 * Self-contained: it re-emits the executionQueue and, on a successful ack,
 * applies the methods locally and saves — WITHOUT re-invoking the original
 * caller's completion/callback (that already resolved with SESSION_EXPIRED so
 * the UI could move on). The session guard invokes this after the session is
 * restored and the socket has reconnected with a fresh token, so the user's
 * edit lands instead of being lost. Mirrors the ack-success branch of
 * makeMutation minus the completion call.
 */
export function replayServerMutation({
  methods,
  factoryEngine,
  tournamentIds,
  saveLocal,
  warmCache,
}: {
  methods: any[];
  factoryEngine: any;
  tournamentIds: string[];
  saveLocal?: boolean;
  warmCache?: boolean;
}): void {
  const ackCallback = (ack: any) => {
    const missingTournament = ack?.error?.code === 'ERR_MISSING_TOURNAMENT';
    if (!serverConfig.get().serverFirst || !(ack?.success || missingTournament)) return;
    const serverMethods = Array.isArray(ack?.appliedServerMethods) ? ack.appliedServerMethods : [];
    const methodsToApply = serverMethods.length ? [...methods, ...serverMethods] : methods;
    const result = engineExecution({ factoryEngine, methods: methodsToApply });
    if (result.error) return;
    void localSave(saveLocal || missingTournament);
  };
  emitTmx({
    data: { type: 'executionQueue', payload: { methods, tournamentIds, rollbackOnError: true, warmCache } },
    ackCallback,
  });
}

// Provider mutation gating lives at constants/mutationPermissions.ts
// (imported as `isMutationAllowed`).
